import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Chip,
  Stack,
  alpha,
  CircularProgress,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import http from '../../../pages/http-common';

// ----------------------------------------------------------------------

const getTargetRoute = (category) => {
  const cat = (category || '').toLowerCase();
  if (cat.includes('saving')) return '/dashboard/savings';
  if (cat.includes('share')) return '/dashboard/shares';
  if (cat.includes('loan')) return '/dashboard/loans';
  if (cat.includes('business')) return '/dashboard/business';
  if (cat.includes('dev')) return '/dashboard/dev';
  if (cat.includes('wel')) return '/dashboard/wel';
  if (cat.includes('min')) return '/dashboard/min';
  if (cat.includes('ileya')) return '/dashboard/ileya';
  if (cat.includes('takaful')) return '/dashboard/takaful';
  if (cat.includes('speacc') || cat.includes('special')) return '/dashboard/speAcc';
  if (cat.includes('profile') || cat.includes('security') || cat.includes('allocation')) return '/dashboard/settings';
  return '/dashboard/app';
};

const getRoleConfig = (role) => {
  const r = (role || '').toLowerCase().replace(/^role_/, '');
  if (r.includes('financial_1') || r === 'financial i' || r === 'financial') {
    return { label: 'Financial I', color: '#059669', bg: '#ECFDF5', icon: 'eva:credit-card-fill' };
  }
  if (r.includes('financial_2') || r === 'financial ii') {
    return { label: 'Financial II', color: '#0284C7', bg: '#F0F9FF', icon: 'eva:briefcase-fill' };
  }
  if (r.includes('financial_3') || r === 'financial iii') {
    return { label: 'Financial III', color: '#7C3AED', bg: '#FAF5FF', icon: 'eva:pie-chart-fill' };
  }
  if (r.includes('registrar') || r.includes('secretary')) {
    return { label: 'Registrar', color: '#2563EB', bg: '#EFF6FF', icon: 'eva:person-done-fill' };
  }
  if (r.includes('president')) {
    return { label: 'President', color: '#D97706', bg: '#FFFBEB', icon: 'eva:shield-fill' };
  }
  if (r.includes('admin')) {
    return { label: 'Admin', color: '#DC2626', bg: '#FEF2F2', icon: 'eva:settings-2-fill' };
  }
  return { label: role || 'Officer', color: '#475569', bg: '#F1F5F9', icon: 'eva:bell-fill' };
};

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth?.user);
  const userId = currentUser?._id || currentUser?.id || 'guest';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  // Read notifications stored in localStorage per user
  const storageKey = `mtc_read_notifs_${userId}`;
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch {
      return [];
    }
  });

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const res = await http.get('/user/notifications');
      if (res && Array.isArray(res.data)) {
        setNotifications(res.data);
      }
    } catch (err) {
      // silently handle network errors during polling
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 45000); // Poll every 45 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setOpen(null);
  };

  const totalUnRead = notifications.filter((item) => !readIds.includes(item._id)).length;

  const handleMarkAllAsRead = () => {
    const allIds = notifications.map((n) => n._id);
    const updated = Array.from(new Set([...readIds, ...allIds]));
    setReadIds(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch {
      // ignore
    }
  };

  const handleItemClick = (notification) => {
    if (!readIds.includes(notification._id)) {
      const updated = [...readIds, notification._id];
      setReadIds(updated);
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch {
        // ignore
      }
    }
    handleClose();
    const targetRoute = getTargetRoute(notification.category);
    navigate(targetRoute);
  };

  const unreadNotifications = notifications.filter((n) => !readIds.includes(n._id));
  const readNotifications = notifications.filter((n) => readIds.includes(n._id));

  return (
    <>
      <IconButton
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          ...(totalUnRead > 0 && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          }),
        }}
      >
        <Badge badgeContent={totalUnRead} color="error" max={99}>
          <Iconify icon="eva:bell-fill" width={22} height={22} />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: { xs: 340, sm: 400 },
            borderRadius: 2.5,
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.16)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5, bgcolor: 'background.neutral' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Account Notifications
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {totalUnRead > 0
                ? `${totalUnRead} new update${totalUnRead > 1 ? 's' : ''} from officers`
                : 'All officer updates are caught up'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {loading && <CircularProgress size={16} />}
            {totalUnRead > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" color="primary" onClick={handleMarkAllAsRead}>
                  <Iconify icon="eva:done-all-fill" width={20} height={20} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ maxHeight: 420 }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                  color: 'primary.main',
                }}
              >
                <Iconify icon="eva:bell-off-outline" width={28} height={28} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                No recent officer activities
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', maxWidth: 280, mx: 'auto' }}>
                Any actions performed on your account by the Registrar or Financial Officers will appear here in real-time.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {unreadNotifications.length > 0 && (
                <>
                  <ListSubheader
                    disableSticky
                    sx={{
                      py: 1,
                      px: 2.5,
                      typography: 'overline',
                      fontWeight: 800,
                      color: 'primary.main',
                      bgcolor: 'transparent',
                    }}
                  >
                    New Updates ({unreadNotifications.length})
                  </ListSubheader>
                  {unreadNotifications.map((notification) => (
                    <NotificationItem
                      key={notification._id}
                      notification={notification}
                      isUnRead={true}
                      onClick={() => handleItemClick(notification)}
                    />
                  ))}
                </>
              )}

              {readNotifications.length > 0 && (
                <>
                  <ListSubheader
                    disableSticky
                    sx={{
                      py: 1,
                      px: 2.5,
                      typography: 'overline',
                      fontWeight: 800,
                      color: 'text.secondary',
                      bgcolor: 'transparent',
                    }}
                  >
                    Earlier Activity
                  </ListSubheader>
                  {readNotifications.map((notification) => (
                    <NotificationItem
                      key={notification._id}
                      notification={notification}
                      isUnRead={false}
                      onClick={() => handleItemClick(notification)}
                    />
                  ))}
                </>
              )}
            </List>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:refresh-fill" />}
            onClick={fetchNotifications}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
          >
            Refresh
          </Button>

          <Button
            size="small"
            onClick={() => {
              handleClose();
              navigate('/dashboard/summary');
            }}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
          >
            View Account Summary
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    performerName: PropTypes.string,
    performerRole: PropTypes.string,
    action: PropTypes.string,
    category: PropTypes.string,
    details: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  isUnRead: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function NotificationItem({ notification, isUnRead, onClick }) {
  const roleConfig = getRoleConfig(notification.performerRole);

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        transition: 'background-color 0.15s ease',
        ...(isUnRead && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: roleConfig.bg,
            color: roleConfig.color,
            border: `1.5px solid ${alpha(roleConfig.color, 0.3)}`,
            width: 40,
            height: 40,
          }}
        >
          <Iconify icon={roleConfig.icon} width={20} height={20} />
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
              {roleConfig.label}
            </Typography>
            <Chip
              label={notification.category || 'General'}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 20,
                bgcolor: alpha(roleConfig.color, 0.1),
                color: roleConfig.color,
              }}
            />
          </Stack>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                color: 'text.primary',
                lineHeight: 1.4,
                mb: 0.5,
                fontWeight: isUnRead ? 600 : 400,
              }}
            >
              {notification.details}
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.disabled',
                  fontSize: '0.725rem',
                }}
              >
                <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 14, height: 14 }} />
                {fToNow(notification.createdAt)}
              </Typography>

              {isUnRead && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                  }}
                />
              )}
            </Stack>
          </Box>
        }
      />
    </ListItemButton>
  );
}
